import { SubChartBaseModel } from '@src/slide/domain/ui-model/element-modeles/base-modeles/chart-base-model';
import {
    SubChartConverterFactory,
} from '@src/slide/domain/ui-model/converter/node-property/module-property/chart/plot-area/sub-chart/sub-chart-converter-factory';
import { toJSON } from '@src/slide/infrastructure/module/independent/object-operation';
import { getPathsFromObject } from '@src/slide/infrastructure/module/independent/object/object-path';
import { PresetChartType } from '../chart-presets/const';
import type { SubChartType } from '@src/slide/domain/ui-model/element-modeles/Interface/chart-v3/sdk-model.interface';
import type { ChartContext } from '@src/slide/domain/ui-model/element-modeles/chart-models/context';
import type {
    IBaseSubChartProperty,
} from '@src/slide/domain/ui-model/element-modeles/property/chart/sub-chart/base-sub-chart.interface';
import type {
    IChartSeries,
    IDataLabels,
} from '@src/slide/domain/ui-model/element-modeles/Interface/chart-v3/sub-chart/sub-chart-properties.interface';
import type { IChartAxis } from '@src/slide/domain/ui-model/element-modeles/Interface/chart-v3/chart-properties.interface';
import type {
    IBaseSubChart,
} from '@src/slide/domain/ui-model/element-modeles/Interface/chart-v3/sub-chart/base-sub-chart.interface';
import type { SerRules } from '@src/slide/domain/ui-model/element-modeles/chart-models/sub-chart/series';
import type { IChartDataModel, ISubChartData, ISubChartSerData } from '@tencent/slide-sdk-model';
import cloneDeep from 'lodash/cloneDeep';
import has from 'lodash/has';
import set from 'lodash/set';
import get from 'lodash/get';
import forEach from 'lodash/forEach';
import isEmpty from 'lodash/isEmpty';
import isNil from 'lodash/isNil';
import mergeWith from 'lodash/mergeWith';
import isObject from 'lodash/isObject';
import { getThemeColorValueByIndex } from '../../property/chart/theme';

export abstract class BaseSubChart extends SubChartBaseModel implements IBaseSubChart {
    // 供v2调用，subChart sdkToUi
    public static async convertDataSdkToUi(sdkData, uiData, rawSdkData, option): Promise<void> {
        // V3的subChartModel没有对应的sdkData，所以这里直接通过type获取converter进行转换
        const converter = SubChartConverterFactory.getSubChartConverterByType(uiData.type);
        converter?.convertSubChartDataSdkToUi(sdkData, uiData);
    }

    // 这个方法在子类均实现重写，原因是在这里取不到子类static 属性的值，需要在子类里去指定
    public static convertDataUiToSdk(uiData, sdkData, rawUiData): void {}

    public static didConvertDataSdkToUi(sdkData, uiData, rawSdk, optionObject) {
        super.didConvertDataSdkToUi(sdkData, uiData, rawSdk, optionObject);
    }

    protected static convertSubChartUiToSdkByType(subChartType: SubChartType, uiData, sdkData, rawUiData) {
        const converter = SubChartConverterFactory.getSubChartConverterByType(subChartType);
        converter?.convertSubChartDataUiToSdk(uiData, sdkData);
    }

    // @ts-ignore  todo v2下掉可以去掉
    declare public type: SubChartType;
    declare public dataLabels?: IDataLabels;
    declare public series: IChartSeries[];
    // @ts-ignore   todo v2下掉可以去掉
    public id: string;
    public originType: SubChartType;
    public varyColors: boolean;
    public axisIds?: number[];
    public axesAddIndex?: number;
    public axes?: IChartAxis[];
    public serRules: SerRules;
    public undoRedo: boolean;   // 2.0 子表有独立Model, 需要携带undoRedo。3.0不会使用此属性
    protected subChartProperty: IBaseSubChartProperty;
    private context: ChartContext;

    // 2.0 此id初始化的时候为空，在baseModel里sdkToUi的时候会赋值sdkData的id
    // 3.0 不会subChart对应的sdkModel，是在chartModel中初始化的时候赋值的id（索引）
    public constructor(id) {
        super();
        this.id = id;
    }

    public getContext() {
        return this.context;
    }

    public setContext(context: ChartContext) {
        this.context = context;
    }

    public getSubChartProperty() {
        return this.subChartProperty;
    }

    public setSubChartProperty(subChartProperty: IBaseSubChartProperty) {
        this.subChartProperty = subChartProperty;
    }

    public getPresetChartType(): PresetChartType {
        return PresetChartType.COLUMN_CLUSTERED;
    }

    /**
     * 更新属性
     * V3的subChart不会bindSDK，所以需要重写下判定
     * @param {*} props 要变更的属性
     */
    public updateProps(props: any): void {
        window.log.info(`${Object.getPrototypeOf(this).constructor.name}updateProps`, toJSON(props));

        const sdkData = this.prepareSDKPropsData(props);

        this.updateSubChartPropsWithSdkDataPatch(sdkData);
    }

    public removeProps(props): void {
        const sdkData = this.prepareSDKPropsData(props);

        this.updateSubChartPropsWithSdkDataPatch(sdkData);
    }

    public updateAndRemoveProps(updateProps, removeProps?) {
        const sUpdateProps = this.prepareSDKPropsData(updateProps);
        const sRemoveProps = this.prepareSDKPropsData(removeProps);
        const toRemovePaths = getPathsFromObject(sRemoveProps);
        toRemovePaths.forEach((path) => {
            // removeProps中目前可能包含undefined，如：[, null]表示删除第二个，所以这里做一下判断
            if (!has(sUpdateProps, path) && get(sRemoveProps, path) === null) {
                set(sUpdateProps, path, null);
            }
        });

        this.updateSubChartPropsWithSdkDataPatch(sUpdateProps);
    }

    public replaceSeries(series: ISubChartSerData[]) {
        const subChartSdkData = this.getSubChartSdkData();

        subChartSdkData.ser = series;

        this.updateSubChartSdkData(subChartSdkData);
    }

    public getSeriesSdkData(): ISubChartSerData[] {
        return this.series.map(ser => Object.getPrototypeOf(ser).constructor.convertDataUiToSdk(ser));
    }

    // 根据新值，更新seriesModel上的一些状态
    public updateSeriesWithNewEditValue() {
        const chartModel = this.context.getChartModel();
        // 需要从新的chartModel上计算
        const totalSeriesCount = chartModel.plotArea.charts
            .reduce((count, subChart) => count + (subChart.series?.length || 0), 0);
        this.series.forEach((ser) => {
            // 更新valueCount，用于编辑数据后更新展示数据长度
            ser.valueCount = ser.dataList.length;

            const isSerNameValid = ser.name && ser.name.trim() !== '';
            // 如果ser的名称不为空，更新name相关联的状态
            if (isSerNameValid && ser.isEmptySeriesName) {
                ser.isEmptySeriesName = false;
            }

            // 如果 是nr，且categoryPoint有不是数字的，就改成str，且把全局的category format改到每个里面dataPoint里（参照金山）
            if (ser.categoryType === 'nr' || ser.categoryType === 'nl') {
                const hasNaNCategory = ser.dataList.find(dataPoint => dataPoint.categoryPoint?.value
                    && isNaN(Number(dataPoint.categoryPoint?.value)));
                if (hasNaNCategory) {
                    ser.categoryType = 'sr';
                    ser.dataList.forEach((dataPoint) => {
                        if (dataPoint.categoryPoint) {
                            dataPoint.categoryPoint.formatCode = ser.categoryFormatCode;
                        }
                    });
                    ser.categoryFormatCode = undefined;
                }
            }
            // 因为数量和index变化，需要变更图形用到的主题色
            if (this.varyColors) {
                ser.dataList.forEach((dataItem, index) => {
                    const color = getThemeColorValueByIndex(
                        index,
                        chartModel.chartSpace?.colorStyle?.id as string, ser.dataList.length,
                    );
                    const dataItemProperty = this.subChartProperty.data[dataItem.idx!];
                    if (isNil(dataItem.dataPoint) || dataItemProperty?.autoThemeColor) {
                        set(dataItem, `dataPoint.props.style.${this.subChartProperty.getGeometryColorPath()}`, color);
                    }
                });
            } else {
                const color = getThemeColorValueByIndex(
                    ser.idx!,
                    chartModel.chartSpace?.colorStyle?.id as string, totalSeriesCount,
                );
                const serProperty = this.subChartProperty.series
                    .find(serProperty => serProperty.idx === ser.originIdx);
                if (serProperty?.autoThemeColor) {
                    // 如果原来是自动主题颜色， 这里需要更新样式颜色
                    set(ser.props.style, this.subChartProperty.getGeometryColorPath(), color);
                }
            }
        });
    }

    protected prepareSDKPropsData(chartData): any {
        const sdkData = {};
        // 深拷贝防止批量updateProps传入的chartData引用被改
        Object.getPrototypeOf(this).constructor.convertDataUiToSdk(cloneDeep(chartData), sdkData, this);

        return sdkData;
    }

    private mergeDiffWithSubChartSdkData = (sdkDataDiff) => {
        const subChartSdkData = this.getSubChartSdkData();

        mergeWith(subChartSdkData, sdkDataDiff, this.mergeSubChartSdkData);
        // 在merge后可能存在有null、undefined的数据
        // 这里将这些无意义的值都进行删除，生成一个干净的subChart数据
        this.deleteNilValueKeyFromData(subChartSdkData);

        // 特殊处理下数据标签，为空的数据标签，避免导出后office不符合预期的默认兜底
        this.deleteCategoriesEmptyValueKey(subChartSdkData);

        return subChartSdkData;
    };

    private mergeSubChartSdkData = (objValue, srcValue, key) => {
        if (key === 'egColorChoice') {
            return srcValue; // 如果key是'egColorChoice'，则返回来源对象的值，不进行合并
        }
        if (isObject(srcValue) && isObject(objValue)) {
            return mergeWith(objValue, srcValue, this.mergeSubChartSdkData);
        }
        return undefined; // 对于其他属性，返回undefined，让lodash处理合并
    };

    private updateSubChartPropsWithSdkDataPatch(sdkDataPatch: ISubChartData) {
        const subChartSdkData = this.mergeDiffWithSubChartSdkData(sdkDataPatch);

        this.updateSubChartSdkData(subChartSdkData);
    }

    private deleteNilValueKeyFromData(obj) {
        // 删除无意义的为null或者undefined的key
        forEach(obj, (value, key) => {
            if (value === undefined || value === null) {
                delete obj[key];
            } else if (typeof value === 'object') {
                this.deleteNilValueKeyFromData(value);
            }
        });
        return obj;
    }

    // 特殊处理下数据标签，为空的数据标签，避免导出后office不符合预期的默认兜底
    private deleteCategoriesEmptyValueKey(subChartSdkData: ISubChartData) {
        subChartSdkData.ser?.forEach((serItem) => {
            serItem.categories.forEach((item) => {
                if (item.dataLabel && isEmpty(item.dataLabel)) {
                    delete item.dataLabel;
                }
            });
        });
    }

    private getSubChartSdkData(): ISubChartData {
        const chartSdkData = this.context.getChartModel().sdkData as IChartDataModel;
        // @ts-ignore
        return cloneDeep(chartSdkData.store.subChartList?.[Number(this.id)]);
    }

    private updateSubChartSdkData(subChartSdkData: ISubChartData) {
        const chartModel = this.context.getChartModel();
        const subChartIndex = Number(this.id);

        (chartModel.sdkData as IChartDataModel)?.api?.updateSubChartProps?.(subChartSdkData, subChartIndex);
    }
}
